import { useCallback, useEffect, useRef, useState, type FormEvent } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { useSignIn } from "@clerk/nextjs/legacy"
import type { SignInResource } from "@clerk/shared/types"
import { Button, Input, Label } from "@coval/ui"

import { logger } from "@/lib/logger"

import { appendEmailParam } from "../utils/emailPrefill"
import {
  extractClerkError,
  isClerkSessionExistsError,
  shouldReportClerkAuthError,
} from "../utils/signInErrors"
import { ClerkAuthFormSkeleton } from "./ClerkAuthFormSkeleton"
import {
  ClerkInvitationSigninStatus,
  EmailCodeSigninForm,
  PasswordSigninForm,
  ResetPasswordSigninForm,
  SecondFactorSigninForm,
  SigninError,
} from "./ClerkSigninFactorForms"
import {
  clerkSigninHeader,
  findEmailCodeFallback,
  findPasswordResetFallback,
  hasBackupCodeFactor,
  pickFirstFactor,
  type ClerkSigninPhase,
} from "./clerkSigninState"
import { GoogleOAuthButton } from "./GoogleOAuthButton"

const log = logger("ClerkSigninForm")

const PASSWORD_RESET_REQUIRED_MESSAGE =
  "Your password needs to be reset before you can sign in. Contact your administrator or use the reset-password flow."
const NO_FACTOR_MESSAGE =
  "No supported sign-in methods are available for this email. Contact support."
const UNSUPPORTED_STEP_MESSAGE =
  "Sign in requires an additional step we don't yet support here. Contact support."
const UNSUPPORTED_FACTOR_MESSAGE =
  "Your account uses a sign-in method we don't yet support here. Contact support."

interface ClerkSigninFormProps {
  onComplete: () => void
  signUpHref: string
  redirectUrlComplete: string
  oauthCallbackUrl?: string
  invitationTicket?: string
}

export function ClerkSigninForm({
  onComplete,
  signUpHref,
  redirectUrlComplete,
  oauthCallbackUrl = "/sso-callback",
  invitationTicket,
}: ClerkSigninFormProps) {
  const { isLoaded, signIn, setActive } = useSignIn()
  const router = useRouter()
  const [phase, setPhase] = useState<ClerkSigninPhase>({
    kind: "email_entry",
  })
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [code, setCode] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [oauthPending, setOauthPending] = useState(false)
  const [ticketPending, setTicketPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const handledTicketRef = useRef<string | null>(null)
  const invitationAttemptPending =
    Boolean(invitationTicket) &&
    (ticketPending || handledTicketRef.current !== invitationTicket)

  const completeFromSession = useCallback(
    async (sessionId: string) => {
      if (!setActive) {
        return
      }
      await setActive({ session: sessionId })
      onComplete()
    },
    [onComplete, setActive],
  )

  const acceptInvitationTicket = useCallback(async () => {
    if (!invitationTicket || !signIn || !setActive) {
      return
    }

    handledTicketRef.current = invitationTicket
    setTicketPending(true)
    setError(null)

    try {
      const attempt = await signIn.create({
        strategy: "ticket",
        ticket: invitationTicket,
      })
      if (attempt.status !== "complete" || !attempt.createdSessionId) {
        setError("We couldn't accept this invitation. Please try again.")
        return
      }
      await setActive({ session: attempt.createdSessionId })
      onComplete()
    } catch (err) {
      // Already signed in: the ticket create can't mint a session (session_exists).
      // The invite still needs consuming, so send them to /select-org to accept it
      // rather than erroring out.
      if (isClerkSessionExistsError(err)) {
        router.replace("/select-org").catch((redirectError: unknown) => {
          // Nav failed — show an error (with retry) instead of a stuck spinner.
          log.error("failed to redirect to /select-org", redirectError)
          setError("We couldn't open your invitations. Please try again.")
        })
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("invitation signin failed", err)
      }
      setError(extractClerkError(err, "Failed to accept this invitation."))
    } finally {
      setTicketPending(false)
    }
  }, [invitationTicket, onComplete, router, setActive, signIn])

  useEffect(() => {
    if (
      !isLoaded ||
      !invitationTicket ||
      !signIn ||
      !setActive ||
      handledTicketRef.current === invitationTicket
    ) {
      return
    }

    void acceptInvitationTicket()
  }, [acceptInvitationTicket, invitationTicket, isLoaded, setActive, signIn])

  async function handleAttemptOutcome(attempt: SignInResource) {
    if (attempt.status === "complete" && attempt.createdSessionId) {
      await completeFromSession(attempt.createdSessionId)
      return
    }
    if (attempt.status === "needs_second_factor") {
      const hasBackup = hasBackupCodeFactor(attempt.supportedSecondFactors)
      setCode("")
      setPhase({ kind: "second_factor", mode: "totp", hasBackup })
      return
    }
    if (attempt.status === "needs_new_password") {
      setError(PASSWORD_RESET_REQUIRED_MESSAGE)
      return
    }
    setError(UNSUPPORTED_STEP_MESSAGE)
  }

  async function startSsoRedirect(identifier: string) {
    if (!signIn) {
      return
    }
    setPhase({ kind: "redirecting_sso" })
    await signIn.authenticateWithRedirect({
      strategy: "enterprise_sso",
      identifier,
      redirectUrl: oauthCallbackUrl,
      redirectUrlComplete,
    })
  }

  async function startEmailCode(
    emailAddressId: string,
    safeIdentifier: string,
  ) {
    if (!signIn) {
      return
    }
    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId,
    })
    setCode("")
    setPhase({ kind: "email_code", emailAddressId, safeIdentifier })
  }

  async function startPasswordReset(
    emailAddressId: string,
    safeIdentifier: string,
  ) {
    if (!signIn || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await signIn.prepareFirstFactor({
        strategy: "reset_password_email_code",
        emailAddressId,
      })
      setCode("")
      setPassword("")
      setPhase({ kind: "reset_password", emailAddressId, safeIdentifier })
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin reset_password prepare failed", err)
      }
      setError(extractClerkError(err, "Couldn't start a password reset."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signIn || submitting) {
      return
    }
    const trimmed = email.trim()
    if (!trimmed) {
      return
    }
    setError(null)
    setSubmitting(true)
    setPhase({ kind: "resolving_factors" })
    try {
      const attempt = await signIn.create({ identifier: trimmed })

      if (attempt.status === "complete" && attempt.createdSessionId) {
        await completeFromSession(attempt.createdSessionId)
        return
      }

      if (attempt.status === "needs_new_password") {
        setError(PASSWORD_RESET_REQUIRED_MESSAGE)
        setPhase({ kind: "email_entry" })
        return
      }

      const factor = pickFirstFactor(attempt.supportedFirstFactors)
      if (!factor) {
        setError(NO_FACTOR_MESSAGE)
        setPhase({ kind: "email_entry" })
        return
      }

      if (factor.strategy === "enterprise_sso") {
        await startSsoRedirect(trimmed)
        return
      }
      if (factor.strategy === "email_code") {
        await startEmailCode(factor.emailAddressId, factor.safeIdentifier)
        return
      }
      if (factor.strategy === "password") {
        setPassword("")
        setPhase({
          kind: "password",
          emailCode: findEmailCodeFallback(attempt.supportedFirstFactors),
          resetPassword: findPasswordResetFallback(
            attempt.supportedFirstFactors,
          ),
        })
        return
      }
      setError(UNSUPPORTED_FACTOR_MESSAGE)
      setPhase({ kind: "email_entry" })
    } catch (err) {
      if (isClerkSessionExistsError(err)) {
        onComplete()
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("signin identifier failed", err)
      }
      setError(extractClerkError(err, "We couldn't start sign in."))
      setPhase({ kind: "email_entry" })
    } finally {
      setSubmitting(false)
    }
  }

  async function handleEmailCodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signIn || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code: code.trim(),
      })
      await handleAttemptOutcome(attempt)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin email_code attempt failed", err)
      }
      setError(extractClerkError(err, "Invalid verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResendEmailCode() {
    if (!signIn || submitting) {
      return
    }
    if (phase.kind !== "email_code") {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: phase.emailAddressId,
      })
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin email_code resend failed", err)
      }
      setError(extractClerkError(err, "Couldn't resend the code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleStartEmailCode(
    emailAddressId: string,
    safeIdentifier: string,
  ) {
    if (!signIn || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await startEmailCode(emailAddressId, safeIdentifier)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin email_code prepare failed", err)
      }
      setError(extractClerkError(err, "Couldn't send the code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signIn || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: "password",
        password,
      })
      await handleAttemptOutcome(attempt)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin password attempt failed", err)
      }
      setError(
        extractClerkError(err, "Failed to sign in. Check your credentials."),
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResetPasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signIn || submitting) {
      return
    }
    if (phase.kind !== "reset_password") {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: code.trim(),
        password,
      })
      await handleAttemptOutcome(attempt)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin reset_password attempt failed", err)
      }
      setError(extractClerkError(err, "Couldn't reset your password."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResendResetCode() {
    if (!signIn || submitting) {
      return
    }
    if (phase.kind !== "reset_password") {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await signIn.prepareFirstFactor({
        strategy: "reset_password_email_code",
        emailAddressId: phase.emailAddressId,
      })
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin reset_password resend failed", err)
      }
      setError(extractClerkError(err, "Couldn't resend the code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSecondFactorSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signIn || submitting) {
      return
    }
    if (phase.kind !== "second_factor") {
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const attempt = await signIn.attemptSecondFactor({
        strategy: phase.mode,
        code: code.trim(),
      })
      if (attempt.status === "complete" && attempt.createdSessionId) {
        await completeFromSession(attempt.createdSessionId)
        return
      }
      setError("That code didn't complete sign in. Try again.")
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signin second_factor failed", err)
      }
      setError(extractClerkError(err, "Invalid verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogleSignIn() {
    if (!signIn || oauthPending) {
      return
    }
    setError(null)
    setOauthPending(true)
    try {
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: oauthCallbackUrl,
        redirectUrlComplete,
      })
    } catch (err) {
      if (isClerkSessionExistsError(err)) {
        onComplete()
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("google signin failed", err)
      }
      setError(extractClerkError(err, "Failed to start Google sign-in."))
      setOauthPending(false)
    }
  }

  function resetToEmailEntry() {
    if (submitting) {
      return
    }
    setPassword("")
    setCode("")
    setError(null)
    setPhase({ kind: "email_entry" })
  }

  if (!isLoaded) {
    return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
  }

  if (invitationTicket) {
    return (
      <ClerkInvitationSigninStatus
        error={error}
        pending={invitationAttemptPending}
        ticketPending={ticketPending}
        onRetry={() => {
          handledTicketRef.current = null
          void acceptInvitationTicket()
        }}
      />
    )
  }

  if (phase.kind === "email_code") {
    return (
      <EmailCodeSigninForm
        phase={phase}
        code={code}
        error={error}
        submitting={submitting}
        onCodeChange={setCode}
        onResend={() => {
          void handleResendEmailCode()
        }}
        onReset={resetToEmailEntry}
        onSubmit={(event) => {
          void handleEmailCodeSubmit(event)
        }}
      />
    )
  }

  if (phase.kind === "password") {
    return (
      <PasswordSigninForm
        phase={phase}
        email={email}
        password={password}
        error={error}
        submitting={submitting}
        onPasswordChange={setPassword}
        onReset={resetToEmailEntry}
        onStartEmailCode={(emailAddressId, safeIdentifier) => {
          void handleStartEmailCode(emailAddressId, safeIdentifier)
        }}
        onStartPasswordReset={(emailAddressId, safeIdentifier) => {
          void startPasswordReset(emailAddressId, safeIdentifier)
        }}
        onSubmit={(event) => {
          void handlePasswordSubmit(event)
        }}
      />
    )
  }

  if (phase.kind === "reset_password") {
    return (
      <ResetPasswordSigninForm
        phase={phase}
        code={code}
        password={password}
        error={error}
        submitting={submitting}
        onCodeChange={setCode}
        onPasswordChange={setPassword}
        onResend={() => {
          void handleResendResetCode()
        }}
        onReset={resetToEmailEntry}
        onSubmit={(event) => {
          void handleResetPasswordSubmit(event)
        }}
      />
    )
  }

  if (phase.kind === "second_factor") {
    return (
      <SecondFactorSigninForm
        phase={phase}
        code={code}
        error={error}
        submitting={submitting}
        onCodeChange={setCode}
        onToggleMode={() => {
          setCode("")
          setError(null)
          setPhase({
            kind: "second_factor",
            mode: phase.mode === "totp" ? "backup_code" : "totp",
            hasBackup: true,
          })
        }}
        onSubmit={(event) => {
          void handleSecondFactorSubmit(event)
        }}
      />
    )
  }

  const headerLabel = clerkSigninHeader(phase.kind)
  const isResolving =
    phase.kind === "resolving_factors" || phase.kind === "redirecting_sso"
  const anyPending = submitting || oauthPending || isResolving
  // Carry whatever they've typed over to sign-up, so a user who tried to sign
  // in, found they have no account, and clicked "Create an account" doesn't
  // have to retype their email.
  const signUpHrefWithEmail = appendEmailParam(signUpHref, email)

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-foreground text-center md:text-left">
          {headerLabel}
        </h1>
      </div>

      <GoogleOAuthButton
        onClick={() => {
          void handleGoogleSignIn()
        }}
        disabled={anyPending}
        label={oauthPending ? "Redirecting…" : "Continue with Google"}
      />

      <div className="flex items-center gap-3" aria-hidden>
        <div className="h-px flex-1 bg-coval-border" />
        <span className="text-xs text-coval-text-muted">or</span>
        <div className="h-px flex-1 bg-coval-border" />
      </div>

      <form
        onSubmit={(event) => {
          void handleEmailSubmit(event)
        }}
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
      >
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-email">
            Work email
          </Label>
          <Input
            id="signin-email"
            name="username"
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            aria-label="Work email"
            data-testid="signin-email-input"
          />
        </div>

        <div id="clerk-captcha" />

        <SigninError error={error} />

        <Button
          type="submit"
          className="w-full"
          disabled={anyPending || !email.trim()}
          aria-label="Continue"
          data-testid="signin-email-submit"
        >
          {isResolving ? "Continuing…" : "Continue"}
        </Button>

        <p className="text-sm text-coval-text-muted">
          New to Coval?{" "}
          <Link
            href={signUpHrefWithEmail}
            className="underline hover:text-foreground"
          >
            Create an account
          </Link>
        </p>
      </form>
    </div>
  )
}
